import React, { useEffect } from "react";
import { BUTTON_MUTED, BUTTON_PRIMARY, PANEL_CLASS } from "./uiConstants";

type TutorialModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tutorial-modal-title"
      onClick={handleBackdropClick}
    >
      <div className={`${PANEL_CLASS} relative w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6 shadow-2xl`}>
        <button
          type="button"
          onClick={onClose}
          className={`absolute right-4 top-4 ${BUTTON_MUTED}`}
        >
          Close
        </button>

        <div className="space-y-4 text-left text-sm text-slate-800">
          <header className="space-y-2">
            <h2 id="tutorial-modal-title" className="text-2xl font-semibold text-slate-900">
              How to Play VOW - Voices of Wetland
            </h2>
            <p>
              VOW is a cooperative strategy game where players steward a wetland through
              ten years of seasonal change. Each role represents a stakeholder with unique
              goals, and together you balance habitat health, biodiversity, and livelihoods.
            </p>
          </header>

          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Game Flow</h3>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Act Setup:</strong> The host advances the game from one act to the next,
                reviewing scores, board state, and water management decisions.
              </li>
              <li>
                <strong>Action Phase:</strong> Each player selects an available action and chooses
                tiles on the board. Actions may graze reedbeds, harvest resources, or install nests.
                The host can finalize actions once everyone is ready.
              </li>
              <li>
                <strong>Simulation Phase:</strong> Players cast a vote for a Water Management (WM)
                scenario. The host tallies the decision, runs the simulation, and the board updates
                for the next act.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Player Roles</h3>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong>Stock Breeder:</strong> Focus on grazing meadows responsibly to support livestock.</li>
              <li><strong>Reed Harvester:</strong> Harvest reeds without exhausting reedbeds.</li>
              <li><strong>Hunter:</strong> Manage duck populations and maintain hunting opportunities.</li>
              <li><strong>Conservationist:</strong> Safeguard biodiversity and protect key habitats.</li>
              <li><strong>Bittern Advocate:</strong> Expand bittern habitat and secure successful nests.</li>
            </ul>
            <p>
              Each role has actions with limits per act. The action selector in your control panel
              lists available moves; the board highlights selected tiles, along with protected areas,
              nests, and recent successes.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Water Management (WM)</h3>
            <p>
              During the Simulation Phase, everyone chooses a WM scenario. Each option changes water
              levels across seasons and affects species success:
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li><strong>WM1:</strong> Stable high water year-round.</li>
              <li><strong>WM2:</strong> Spring flooding followed by moderate levels.</li>
              <li><strong>WM3:</strong> Strong summer dry-out with rapid spring refill.</li>
              <li><strong>WM4:</strong> Mild dry-out mid-year, moderate levels otherwise.</li>
              <li><strong>WM5:</strong> Alternates between flooding and deep dry-out.</li>
            </ul>
            <p>
              The host can review votes in the Host Admin panel before running the simulation.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Winning Together</h3>
            <ul className="list-disc space-y-2 pl-5">
              <li>Track the dashboards to understand how each role is performing.</li>
              <li>Use snapshots and logs to review past decisions.</li>
              <li>Coordinate actions before the host finalizes them—communication is key.</li>
              <li>Balance short-term gains with long-term habitat resilience.</li>
            </ul>
          </section>

          <footer className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-600">
              Tip: Use the Map Views to inspect habitats, water levels, and biodiversity before you act.
            </p>
            <button type="button" onClick={onClose} className={BUTTON_PRIMARY}>
              Got it, let's play
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default TutorialModal;
